import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { FileText, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Breadcrumb from '../components/Common/Breadcrumb';
import { Actions as AGREEMENTS } from '../store/actions/agreementsActions';
import { toggleAlertMessage } from '../store/action';

const CustomerAgreementsPage = ({ getMyAgreements, getAgreementSignedPdfUrl, list, loading, showMessage, currentOrganisation }) => {
  useEffect(() => {
    if (currentOrganisation) getMyAgreements();
  }, [currentOrganisation, getMyAgreements]);

  const onPdf = async (id) => {
    const url = await getAgreementSignedPdfUrl(id);
    if (url) window.open(url, '_blank', 'noopener');
    else showMessage(true, 'No PDF available yet', 'error', 3000);
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      <Breadcrumb items={[{ label: 'Agreements' }]} />
      <h1 className="text-2xl font-semibold text-foreground mb-4">Your agreements</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Commercial order forms, signed PDFs, and legacy PandaDoc agreements for your organisation.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText size={18} strokeWidth={1.5} />
            Agreement history
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (!list || list.length === 0) && (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          <div className="border border-border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Term / commitment</TableHead>
                  <TableHead>Accepted</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!list || list.length === 0) && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                      No agreements on file for this organisation.
                    </TableCell>
                  </TableRow>
                )}
                {(list || []).map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">
                      {a.type}
                      {a.isLegacyPandaDoc && (
                        <Badge variant="secondary" className="ml-2 text-xs">Legacy</Badge>
                      )}
                    </TableCell>
                    <TableCell>{a.status}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {a.term?.commitmentEndDate
                        ? `Until ${format(parseISO(a.term.commitmentEndDate), 'PPP')}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {a.acceptedAt ? format(parseISO(a.acceptedAt), 'PPP') : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => a.id && onPdf(a.id)}
                        disabled={!a.id}
                      >
                        <Download size={14} strokeWidth={1.5} className="mr-1" />
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapState = (s) => ({
  list: s.agreements?.list || [],
  loading: s.agreements?.loading?.list,
  currentOrganisation: s.currentOrganisation,
});

const mapDispatch = (d) => ({
  getMyAgreements: (oid) => d(AGREEMENTS.getMyAgreements(oid)),
  getAgreementSignedPdfUrl: (id) => d(AGREEMENTS.getAgreementSignedPdfUrl(id)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(mapState, mapDispatch)(CustomerAgreementsPage);
